import html2canvas from 'html2canvas';
import { logError } from './utils';
import CanvasElementDiscovery from './CanvasElementDiscovery';
import ProjectConfigurationService from '../services/ProjectConfigurationService';
import { EProjectType } from '../enum/project';

/**
 * Enhanced element discovery using unified discovery system
 * @param projectType Project type for configuration-based discovery
 * @param timeoutMs Optional timeout override
 * @returns Promise with discovered canvas elements
 */
export const discoverCanvasElementsUnified = async (
    projectType: EProjectType,
    timeoutMs?: number
): Promise<{
    elements: HTMLCanvasElement[];
    foundIds: string[];
    missingIds: string[];
    allFound: boolean;
}> => {
    try {
        const config = ProjectConfigurationService.getProjectConfig(projectType);
        const options = timeoutMs ? { waitTimeout: timeoutMs } : {};

        return await CanvasElementDiscovery.discoverElements(config, options);
    } catch (error) {
        logError('pdfImageCapture', 'Unified discovery failed:', error);
        throw error;
    }
};

/**
 * Attende che tutti gli elementi con gli id specificati siano presenti nel DOM.
 * @param ids array di stringhe id
 * @param timeoutMs timeout massimo in ms
 * @returns Promise<void>
 */
export const waitForElements = (ids: string[], timeoutMs = 3000): Promise<void> => {
    return new Promise((resolve, reject) => {
        const start = Date.now();
        const check = () => {
            const allPresent = ids.every(id => document.getElementById(id));
            if (allPresent) {
                resolve();
            } else if (Date.now() - start > timeoutMs) {
                reject(new Error('Timeout waiting for elements: ' + ids.join(', ')));
            } else {
                setTimeout(check, 50);
            }
        };
        check();
    });
};

/**
 * Cattura un canvas e lo converte in formato JPEG con sfondo bianco
 * per ridurre significativamente le dimensioni del file rispetto a PNG
 *
 * @param canvas HTMLCanvasElement da convertire in formato JPEG
 * @param quality qualità JPEG (0.0 - 1.0, default: 0.8)
 * @returns string base64 nel formato 'data:image/jpeg;base64,...' standard
 *
 * @example
 * ```typescript
 * const canvas = document.getElementById('myCanvas') as HTMLCanvasElement;
 * const jpegBase64 = captureCanvasAsJpeg(canvas); // JPEG con sfondo bianco
 * ```
 *
 * @since Updated in v1.2 - Changed from PNG to JPEG with white background to reduce file size
 */
export const captureCanvasAsJpeg = (canvas: HTMLCanvasElement, quality = 0.8): string => {
    // Create a new canvas with white background for JPEG format
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;

    const ctx = tempCanvas.getContext('2d');
    if (!ctx) {
        return canvas.toDataURL('image/jpeg', quality);
    }

    // Fill with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    // Draw the original canvas on top
    ctx.drawImage(canvas, 0, 0);

    // Return JPEG format for smaller file size
    return tempCanvas.toDataURL('image/jpeg', quality);
};

/**
 * Cattura un elemento HTML usando html2canvas
 * @param element HTMLElement da catturare
 * @param options opzioni per html2canvas
 * @returns Promise<string> base64 dell'immagine
 */
export const captureElementWithHtml2Canvas = async (
    element: HTMLElement,
    options: {
        scale?: number;
        backgroundColor?: string;
        width?: number;
        height?: number;
        useCORS?: boolean;
        allowTaint?: boolean;
        quality?: number;
        format?: 'png' | 'jpeg';
    } = {}
): Promise<string> => {
    const { scale = 1, backgroundColor = '#FFFFFF', useCORS = true, allowTaint = true, quality = 0.8, format = 'jpeg', ...restOptions } = options;

    const canvas = await html2canvas(element, {
        scale,
        backgroundColor,
        useCORS,
        allowTaint,
        ...restOptions,
    });

    const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
    return canvas.toDataURL(mimeType, format === 'png' ? undefined : quality);
};

/**
 * Trova gli elementi DOM per la cattura Container Yard
 * @returns oggetto con gli elementi DOM trovati
 */
export const findContainerYardElements = () => {
    // For unpaved projects, check for the new element names
    const tireElement1 = document.getElementById('tire-layer-unstabilized') || document.getElementById('tire-layer-unreinforced');
    const tireElement2 = document.getElementById('tire-layer-geogrid') || document.getElementById('tire-layer-reinforced');
    const tireElement3 = document.getElementById('tire-layer-geotextile'); // Third image for geotextile
    const chartElement = document.getElementById('line-chart-results');

    return {
        tireElement1,
        tireElement2,
        tireElement3,
        chartElement,
        canvas1: tireElement1?.querySelector('canvas') as HTMLCanvasElement | null,
        canvas2: tireElement2?.querySelector('canvas') as HTMLCanvasElement | null,
        canvas3: tireElement3?.querySelector('canvas') as HTMLCanvasElement | null,
    };
};

/**
 * Trova gli elementi DOM per la cattura GeoGrid
 * @returns oggetto con gli elementi DOM trovati
 */
export const findGeoGridElements = () => {
    const geogridElement = document.getElementById('canvas-container');
    return { geogridElement };
};
